import { CommonModule } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  inject,
} from '@angular/core';
import {
  ActivatedRoute,
  NavigationEnd,
  Router,
  RouterLink,
  RouterOutlet,
} from '@angular/router';
import { PartnerDatum } from '@core/models/admin/partners';
import { Pagination } from '@core/models/pagination.model';
import { BreadCrumbService } from '@core/services/bread-crumb.service';
import { ConfirmDialogService } from '@core/services/confirm-dialog.service';
import { RolesAndPermissionsService } from '@core/services/roles-and-permissions.service';
import { Store } from '@ngxs/store';
import { ButtonDirective } from '@shared/directives/button.directive';
import { PermissionDirective } from '@shared/directives/permission.directive';
import {
  DropDownModel,
  DropdownComponent,
} from '@shared/ui/dropdown/dropdown.component';
import { FiltersComponent } from '@shared/ui/filters/filters.component';
import { TableNavigatorComponent } from '@shared/ui/table-navigator/table-navigator.component';
import { TableSkeletonLoaderComponent } from '@shared/ui/table-skeleton-loader/table-skeleton-loader.component';
import { IFILTER } from 'app/app.constants';
import { TableActions } from 'app/auth/table-store/table.actions';
import { TableSelectors } from 'app/auth/table-store/table.selectors';
import { TableRefreshComponent } from 'app/ui/table-refresh/table-refresh.component';
import { Observable, Subscription, take } from 'rxjs';

@Component({
  selector: 'app-partners',
  standalone: true,
  imports: [
    CommonModule,
    RouterOutlet,
    RouterLink,
    ButtonDirective,
    DropdownComponent,
    FiltersComponent,
    PermissionDirective,
    TableNavigatorComponent,
    TableRefreshComponent,
    TableSkeletonLoaderComponent,
  ],
  templateUrl: './partners.component.html',
  styleUrl: './partners.component.scss',
})
export class PartnersComponent implements OnInit, OnDestroy {
  subs: Subscription[] = [];
  actionList: DropDownModel[] = [];

  partnerId!: string | null;
  partnerName!: string | null;

  // subscription stats are not returned by the API yet
  totalSubs: number = 0;
  totalActiveSubs: number = 0;

  partnersList$: Observable<PartnerDatum[]>;
  totalCount$: Observable<number>;
  isFetchingPartners$: Observable<boolean>;

  isFilterActive: boolean = false;
  private hadFilterBefore = false;
  private readonly baseUrl: string = '/admin/partners';
  private previousUrl: string = '';

  filterStructure: IFILTER[] = [
    {
      filterKey: 'start_date',
      label: 'Start Date',
      type: 'date',
    },
    {
      filterKey: 'end_date',
      label: 'End Date',
      type: 'date',
    },
  ];

  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);
  private breadCrumbService = inject(BreadCrumbService);
  private confirmDialogService = inject(ConfirmDialogService);
  private roleService = inject(RolesAndPermissionsService);
  private store = inject(Store);
  private change = inject(ChangeDetectorRef);

  constructor() {
    this.partnersList$ = this.store.select(
      TableSelectors.getTableData('partners')
    );
    this.totalCount$ = this.store.select(
      TableSelectors.getTableTotalCount('partners')
    );
    this.isFetchingPartners$ = this.store.select(
      TableSelectors.getTableLoading('partners')
    );

    // reload the list whenever we come back from one of the slide-in modals
    const sub = this.router.events.subscribe((event) => {
      if (!(event instanceof NavigationEnd)) return;

      const currentUrl = event.urlAfterRedirects.split('?')[0];
      const cameFromChildRoute = this.previousUrl.startsWith(
        `${this.baseUrl}/`
      );

      if (currentUrl === this.baseUrl && cameFromChildRoute) {
        this.reloadPartners();
      }
      this.previousUrl = currentUrl;
    });
    this.subs.push(sub);
  }

  ngOnInit(): void {
    this.breadCrumbService.setPageBreadCrumb(this.activatedRoute);
    this.loadPartnersList();
    this.listFiltersEventListern();
  }

  loadPartnersList(page = 1) {
    const sub = this.partnersList$.pipe(take(1)).subscribe((partners) => {
      const hasData = Array.isArray(partners) && partners.length > 0;
      // only dispatch if store is empty
      if (!hasData) {
        this.store.dispatch(
          new TableActions.LoadTableData({
            tableKey: 'partners',
            params: new Pagination({
              skip: (page - 1) * 10,
              size: 10,
            }),
          })
        );
      }
    });
    this.subs.push(sub);
  }

  listFiltersEventListern() {
    const sub = this.activatedRoute.queryParams.subscribe((params) => {
      let query = '';

      if (params['search']) {
        query += `search_text=${encodeURIComponent(params['search'])}&`;
      }
      if (params[this.filterStructure[0].filterKey]) {
        query += `start_date=${params['start_date']}&`;
      }
      if (params[this.filterStructure[1].filterKey]) {
        query += `end_date=${params['end_date']}&`;
      }

      const rawPage = params['page'] ? parseInt(params['page']) : 1;
      const page = rawPage > 1 ? rawPage - 1 : 0;
      const take = 10;
      const skip = page * take;

      if (query) {
        this.isFilterActive = true;
        this.hadFilterBefore = true;
        this.store.dispatch(
          new TableActions.FilterPartnersList({
            filter: `${query}take=${take}&skip=${skip}`,
          })
        );
      } else {
        this.isFilterActive = false;
        // only re-fetch if there was a previous filter
        if (this.hadFilterBefore || params['page']) {
          this.hadFilterBefore = false;
          this.store.dispatch(
            new TableActions.LoadTableData({
              tableKey: 'partners',
              params: new Pagination({ skip: skip, size: take }),
            })
          );
        }
      }
    });
    this.subs.push(sub);
  }

  initDropdownList(partner: PartnerDatum) {
    return (this.actionList = [
      {
        action: () => {
          this.editPartner(partner);
        },
        title: 'Edit',
        show: this.roleService.hasPermission(['can_update_partner']),
      },
      {
        action: () => {
          this.addKycDocument(partner);
        },
        title: 'Add KYC',
        show: this.roleService.hasPermission(['add_partner_kyc_document']),
      },
      {
        action: () => {
          this.partnerId = partner.id;
          this.partnerName = partner.name;
          this.confirmDialogService.openDialog({
            isOpen: true,
            isDialogLoading: false,
            title: 'Yes, Delete Partner',
            description: `Are you sure you want to delete this partner: ${this.partnerName}? This action cannot be undone.`,
            loadingText: 'Deleting Partner...',
            bgConfirmButton: 'danger',
            action: () => this.deletePartner(),
            onClose: () => {
              this.partnerId = null;
              this.partnerName = null;
            },
          });
        },
        title: 'Delete',
        show: this.roleService.hasPermission(['can_delete_partner']),
      },
    ]);
  }

  editPartner(partner: PartnerDatum) {
    this.router.navigate(['edit', partner.id], {
      relativeTo: this.activatedRoute,
      state: { partner },
    });
  }

  addKycDocument(partner: PartnerDatum) {
    this.router.navigate(['add-kyc', partner.id], {
      relativeTo: this.activatedRoute,
      state: { partner },
    });
  }

  deletePartner() {
    if (!this.partnerId) return;
    this.confirmDialogService.updateDialog({ isDialogLoading: true });

    this.store.dispatch(
      new TableActions.DeletePartner({ partnerId: this.partnerId })
    );

    this.confirmDialogService.updateDialog({
      isDialogLoading: false,
      isOpen: false,
    });
    this.partnerId = null;
    this.change.detectChanges();
  }

  navigate(currentPage: number) {
    if (this.isFilterActive) {
      this.router.navigate([], {
        queryParams: { page: currentPage },
        queryParamsHandling: 'merge',
      });
    }
  }

  private reloadPartners() {
    const page = this.activatedRoute.snapshot.queryParams['page'];
    const skip = page && parseInt(page) > 1 ? (parseInt(page) - 1) * 10 : 0;

    this.store.dispatch(
      new TableActions.LoadTableData({
        tableKey: 'partners',
        params: new Pagination({ skip, size: 10 }),
      })
    );
  }

  ngOnDestroy(): void {
    this.subs.forEach((sub) => sub.unsubscribe());
  }
}
